import { useEffect, useMemo, useRef, useState } from 'react';

import { StarIcon } from '@hugeicons-pro/core-solid-rounded';
import { ArrowLeft01Icon, Delete02Icon } from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import cn from 'classnames';
import FeatherIcon from 'feather-icons-react';
import { getFetchBaseQueryErrorMessage } from 'helpers/api';
import { emailFormatError, normalizeEmail } from 'helpers/validators/pattern-validators';
import { applyDefaultBrandingColors } from 'hooks/branding-style-variables';
import { useDocumentTitle } from 'hooks/document-title';
import { useToast } from 'hooks/toast';
import { useLocalStorage } from 'hooks/use-local-storage';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { CandidateExperience } from 'views/Company/tabs/Branding/CandidateExperience/CandidateExperience';
import { Clicktrought } from 'views/Company/tabs/Branding/Clicktrought/Clicktrought';
import { Documents } from 'views/Company/tabs/Branding/Documents/Documents';
import { Emails } from 'views/Company/tabs/Branding/Emails/Emails';
import { General } from 'views/Company/tabs/Branding/General/General';
import { LeadGeneration } from 'views/Company/tabs/Branding/LeadGeneration/LeadGeneration';
import { Reminders } from 'views/Company/tabs/Branding/Reminders/Reminders';
import { Reporting } from 'views/Company/tabs/Branding/Reporting/Reporting';
import { Sms } from 'views/Company/tabs/Branding/Sms/Sms';

import Alert from 'components/Alert/Alert';
import EditorChromeLayout, {
  editorChromeHeaderClassName,
  editorChromeStickyBarClassName,
} from 'components/EditorChromeLayout/EditorChromeLayout';
import { Button, EditableTitle } from 'components/FormComponents';
import { IconButton } from 'components/IconButton/IconButton';
import { Loading } from 'components/Loading/Loading';
import StatusPill from 'components/Pills/StatusPills';

import { selectAuthentication } from 'api/authentication-slice';
import { useUpdateBrandEmailTemplateLinksMutation } from 'api/brands-api';
import {
  addBrandUpload,
  deleteBrand,
  getBrandUploads,
  getBrands,
  removeBrandUpload,
  selectBrands,
  updateBrand,
} from 'api/brands-slice';
import { getCompanyChecks, selectCompanyChecks } from 'api/company-checks-slice';
import { getCompany, selectCompany } from 'api/company-slice';
import { selectUser } from 'api/user-slice';

import BrandingDetailsSidebar, { BRANDING_TAB_IDS } from './BrandingDetailsSidebar';

export const BRANDING_DETAILS_SIDEBAR_KEY = 'branding-details-sidebar-collapsed';

const TAB_META = {
  general: {
    title: 'General',
    description:
      'Set up your brand details used across reports, emails, and candidate communications.',
  },
  email: {
    title: 'Email',
    description:
      'From address, banner, templates, and email behaviour for candidates and referees.',
  },
  sms: {
    title: 'SMS',
    description: 'SMS copy and options for candidate and referee messages.',
  },
  candidate_experience: {
    title: 'Candidate Experience',
    description: 'Introduction text shown to candidates and volunteers in the portal.',
  },
  lead_generation: {
    title: 'Engagement',
    description: 'Engagement and lead capture settings.',
  },
  clicktrough: {
    title: 'Clickthrough Websites',
    description: 'Links shown in emails and on the candidate portal.',
  },
  documents: {
    title: 'Documents',
    description: 'Files available for this brand.',
  },
  reminders: {
    title: 'Reminders',
    description: 'Automated reminder configuration.',
  },
  reporting: {
    title: 'PDF Reports',
    description:
      'Customise branding for your PDF reports. By default, reports inherit checkmate branding.',
  },
};

const CHECKS_WITH_REPORT_CONFIG = ['custom_reference', 'candidate_questionnaire'];

const BrandingDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const showToast = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const [sidebarCollapsed, setSidebarCollapsed] = useLocalStorage(
    BRANDING_DETAILS_SIDEBAR_KEY,
    false,
  );
  const [brandsReady, setBrandsReady] = useState(false);
  const [saving, setSaving] = useState(false);
  const [, setDocumentsBusy] = useState(false);
  const [brandToDelete, setBrandToDelete] = useState(null);

  const brands = useSelector(selectBrands);
  const company = useSelector(selectCompany);
  const companyChecks = useSelector(selectCompanyChecks);
  const user = useSelector(selectUser);
  const authentication = useSelector(selectAuthentication);

  const brandId = id ? Number(id) : null;
  const brand = useMemo(() => brands.find(b => b.id === brandId), [brands, brandId]);

  const companyHasRequiredChecks = useMemo(
    () =>
      !company?.ff_enable_legacy_report &&
      companyChecks.some(cc => CHECKS_WITH_REPORT_CONFIG.includes(cc.type)),
    [companyChecks, company],
  );

  const availableTabIds = useMemo(
    () =>
      companyHasRequiredChecks
        ? BRANDING_TAB_IDS
        : BRANDING_TAB_IDS.filter(tabId => tabId !== 'reporting'),
    [companyHasRequiredChecks],
  );

  const activeTab = availableTabIds.includes(searchParams.get('tab'))
    ? searchParams.get('tab')
    : 'general';
  const tabMeta = TAB_META[activeTab] ?? TAB_META.general;

  const {
    control,
    register,
    reset,
    setValue,
    setError,
    getValues,
    trigger,
    watch,
    handleSubmit,
    formState: { errors, isDirty },
  } = useForm({ defaultValues: brand ?? {} });

  const pendingLinkChanges = useRef({});
  const [unsavedAlert, setUnsavedAlert] = useState(null);
  const isDirtyRef = useRef(false);
  const hasGuardEntry = useRef(false);

  const nameValue = useWatch({ control, name: 'name' });
  const values = useWatch({ control });

  useDocumentTitle(brand?.name ? `Branding · ${brand.name}` : 'Branding');

  const [updateBrandEmailTemplateLinks] = useUpdateBrandEmailTemplateLinksMutation();

  useEffect(() => {
    pendingLinkChanges.current = {};
  }, [brand?.id]);

  useEffect(() => {
    if (!id) {
      navigate('/company/branding', { replace: true });
    }
  }, [id, navigate]);

  useEffect(() => {
    dispatch(getBrands()).finally(() => setBrandsReady(true));
  }, [dispatch]);

  useEffect(() => {
    if (!company?.id && authentication?.company_id) {
      dispatch(getCompany(authentication.company_id));
    }
  }, [company?.id, authentication?.company_id, dispatch]);

  useEffect(() => {
    if (authentication?.company_id && companyChecks.length === 0) {
      dispatch(getCompanyChecks());
    }
  }, [authentication?.company_id, companyChecks.length, dispatch]);

  useEffect(() => {
    if (!brandsReady || !brandId) return;
    if (!brands.some(b => b.id === brandId)) {
      navigate('/company/branding', { replace: true });
    }
  }, [brandsReady, brands, brandId, navigate]);

  useEffect(() => {
    if (brand) reset(applyDefaultBrandingColors(brand));
  }, [brand?.id, reset]);

  useEffect(() => {
    if (activeTab !== 'email') return;
    if (emailFormatError(getValues('brand_email'))) trigger('brand_email');
  }, [activeTab, brand?.brand_email, getValues, trigger]);

  useEffect(() => {
    const handler = e => {
      if (isDirty || Object.keys(pendingLinkChanges.current).length > 0) {
        e.preventDefault();
      }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isDirty]);

  useEffect(() => {
    isDirtyRef.current = isDirty || Object.keys(pendingLinkChanges.current).length > 0;
    if (isDirtyRef.current && !hasGuardEntry.current) {
      window.history.pushState(null, '', window.location.href);
      hasGuardEntry.current = true;
    }
  }, [isDirty]);

  useEffect(() => {
    const handler = () => {
      if (isDirtyRef.current) {
        window.history.pushState(null, '', window.location.href);
        setUnsavedAlert({ onConfirm: () => navigate('/company/branding') });
      } else {
        hasGuardEntry.current = false;
      }
    };
    window.addEventListener('popstate', handler);
    return () => window.removeEventListener('popstate', handler);
  }, [navigate]);

  const onSave = async data => {
    if (!brand) return false;
    data.brand_email = normalizeEmail(data.brand_email);
    const brandEmailError = emailFormatError(data.brand_email);
    if (brandEmailError) {
      setError('brand_email', { type: 'pattern', message: brandEmailError });
      if (activeTab !== 'email') setSearchParams({ tab: 'email' });
      return false;
    }
    setSaving(true);
    try {
      const signatureUpdated =
        data.brand_email &&
        (brand.brand_email !== data.brand_email || brand.brand_name !== data.brand_name);
      const payload = applyDefaultBrandingColors({ ...data, signature_updated: signatureUpdated });
      const { payload: response } = await dispatch(updateBrand({ id: brand.id, data: payload }));
      if (response?.error) {
        showToast('Error', response.error.message, 'error');
        return false;
      }
      showToast(t('branding.saved'), t('branding.savedMessage'), 'success');
      let linksSaved = true;
      if (brand?.id && Object.keys(pendingLinkChanges.current).length > 0) {
        try {
          await updateBrandEmailTemplateLinks({
            id: brand.id,
            ...pendingLinkChanges.current,
          }).unwrap();
          pendingLinkChanges.current = {};
        } catch (error) {
          linksSaved = false;
          showToast(
            'Error',
            getFetchBaseQueryErrorMessage(error) || 'Failed to save email template links.',
            'error',
          );
        }
      }
      reset({ ...response.result, uploads: brand.uploads });
      await dispatch(getBrands());
      return linksSaved;
    } finally {
      setSaving(false);
    }
  };

  const onChangeDefault = async value => {
    if (!brand) return;
    setSaving(true);
    const { payload: response } = await dispatch(
      updateBrand({ id: brand.id, data: { default: value } }),
    );
    if (response?.error) showToast('Error', response.error.message, 'error');
    else {
      showToast(
        'Great work! Brand updated',
        value
          ? 'This brand is now the default brand.'
          : 'This brand is no longer the default brand.',
        'success',
      );
      await dispatch(getBrands());
    }
    setSaving(false);
  };

  const isDirtyOrPending = () => isDirty || Object.keys(pendingLinkChanges.current).length > 0;

  const guardedNavigate = onConfirm => {
    if (isDirtyOrPending()) {
      setUnsavedAlert({ onConfirm });
    } else {
      onConfirm();
    }
  };

  const clearGuard = () => {
    isDirtyRef.current = false;
    hasGuardEntry.current = false;
  };

  const onInvalid = formErrors => {
    if (formErrors.brand_email && activeTab !== 'email') {
      setSearchParams({ tab: 'email' });
    }
  };

  const handleUnsavedSave = async () => {
    const onConfirm = unsavedAlert?.onConfirm;
    setUnsavedAlert(null);
    await handleSubmit(async data => {
      const saved = await onSave(data);
      if (!saved) return;
      clearGuard();
      onConfirm?.();
    }, onInvalid)();
  };

  const handleUnsavedCancel = () => {
    setUnsavedAlert(null);
  };

  const onConfirmDelete = async () => {
    if (brandToDelete == null) return;
    setSaving(true);
    await dispatch(deleteBrand(brandToDelete));
    reset({});
    pendingLinkChanges.current = {};
    clearGuard();
    setBrandToDelete(null);
    setSaving(false);
    navigate('/company/branding');
  };

  const onGetBrandUploads = brandUploadId => dispatch(getBrandUploads(brandUploadId));
  const onRemoveBrandUpload = (brandUploadId, uploadId) =>
    dispatch(removeBrandUpload({ brandId: brandUploadId, uploadId }));
  const onAddBrandUpload = (brandUploadId, upload) =>
    dispatch(addBrandUpload({ brandId: brandUploadId, upload }));

  if (!id) {
    return null;
  }

  if (!brandsReady || !brand) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loading active={true} />
      </div>
    );
  }

  return (
    <>
      {brandToDelete != null && (
        <Alert
          show
          title="Are you sure you want to delete this brand permanently?"
          message="Deletion cannot be undone. Are you sure you want to continue?"
          ok="Delete"
          cancel="Cancel"
          icon={Delete02Icon}
          showConfirmationInput
          type="error"
          onOk={onConfirmDelete}
          onCancel={() => setBrandToDelete(null)}
        />
      )}

      <Alert
        show={unsavedAlert !== null}
        title="You have unsaved changes"
        message="Would you like to save your changes before continuing?"
        ok="Save and continue"
        cancel="Cancel"
        type="warning"
        onOk={handleUnsavedSave}
        onCancel={handleUnsavedCancel}
      />

      <EditorChromeLayout
        sidebarCollapsed={sidebarCollapsed}
        header={
          <header className={editorChromeHeaderClassName()}>
            <div className="flex items-center gap-4 min-w-0 flex-1">
              <HugeiconsIcon
                icon={ArrowLeft01Icon}
                size="32"
                onClick={() => guardedNavigate(() => navigate('/company/branding'))}
                className="cursor-pointer text-neutral-5 shrink-0"
              />
              <div className="flex items-center gap-2 min-w-0">
                <EditableTitle register={register} name="name" value={nameValue} />
                <StatusPill
                  status={brand?.default ? 'warning' : 'disabled'}
                  text="Default"
                  className="px-4 cursor-pointer"
                  icon={StarIcon}
                  iconColor={brand?.default ? 'text-orange-3' : 'text-neutral-5'}
                  noBorder
                  onClick={() => onChangeDefault(!brand?.default)}
                />
              </div>
            </div>
            <div className="flex items-center gap-4 shrink-0">
              {!brand.default && (
                <OverlayTrigger
                  trigger={['hover', 'focus']}
                  placement="bottom"
                  overlay={<Tooltip>Delete brand</Tooltip>}>
                  <HugeiconsIcon
                    icon={Delete02Icon}
                    onClick={() => setBrandToDelete(brand.id)}
                    className="cursor-pointer text-red-2"
                    size="24"
                  />
                </OverlayTrigger>
              )}
              <Button submit disabled={saving} buttonProps={{ form: 'branding-details-form' }}>
                Save
              </Button>
            </div>
          </header>
        }
        sidebar={
          <BrandingDetailsSidebar
            isCollapsed={sidebarCollapsed}
            onToggleCollapsed={setSidebarCollapsed}
            brand={brand}
            showReporting={companyHasRequiredChecks}
            onTabChange={tabId => guardedNavigate(() => setSearchParams({ tab: tabId }))}
          />
        }>
        <form id="branding-details-form" onSubmit={handleSubmit(onSave, onInvalid)}>
          <div className={editorChromeStickyBarClassName(sidebarCollapsed)}>
            <div className="flex flex-col gap-2 min-w-0">
              <span className="text-xl font-semibold text-neutral-6">{tabMeta.title}</span>
              <p className="text-xl text-neutral-6 font-regular m-0">{tabMeta.description}</p>
            </div>
          </div>
          <div className="p-10">
            {activeTab === 'general' && (
              <General
                brand={brand}
                control={control}
                errors={errors}
                register={register}
                backgroundColor="background-secondary"
              />
            )}
            {activeTab === 'email' && (
              <Emails
                brand={brand}
                control={control}
                errors={errors}
                register={register}
                readOnly={false}
                backgroundColor="background-secondary"
                onEmailTemplateLinksChange={changes => {
                  pendingLinkChanges.current = changes;
                }}
              />
            )}
            {activeTab === 'sms' && (
              <Sms
                control={control}
                register={register}
                readOnly={false}
                backgroundColor="background-secondary"
                brand={brand}
              />
            )}
            {activeTab === 'candidate_experience' && (
              <CandidateExperience
                brand={brand}
                control={control}
                hasVolunteer={company?.use_volunteers}
                register={register}
                readOnly={false}
                backgroundColor="background-secondary"
              />
            )}
            {activeTab === 'lead_generation' && (
              <LeadGeneration
                control={control}
                register={register}
                errors={errors}
                brand={brand}
                readOnly={false}
                backgroundColor="background-secondary"
              />
            )}
            {activeTab === 'clicktrough' && (
              <Clicktrought
                brand={brand}
                register={register}
                watch={watch}
                readOnly={false}
                errors={errors}
                backgroundColor="background-secondary"
              />
            )}
            {activeTab === 'documents' && (
              <Documents
                brand={brand}
                control={control}
                register={register}
                readOnly={false}
                getBrandUploads={onGetBrandUploads}
                removeBrandUpload={onRemoveBrandUpload}
                addBrandUpload={onAddBrandUpload}
                setLoading={setDocumentsBusy}
                backgroundColor="background-secondary"
              />
            )}
            {activeTab === 'reminders' && (
              <Reminders
                control={control}
                register={register}
                setValue={setValue}
                readOnly={false}
                backgroundColor="background-secondary"
                brand={brand}
              />
            )}
            {activeTab === 'reporting' && companyHasRequiredChecks && (
              <Reporting
                control={control}
                brand={brand}
                register={register}
                readOnly={false}
                companyChecks={companyChecks}
                backgroundColor="background-secondary"
              />
            )}
          </div>
        </form>
      </EditorChromeLayout>
    </>
  );
};

export default BrandingDetails;
